import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useApi } from '../services/axiosInterceptor';
import { setAvatar, setUser, setPhoneVerified } from '../redux/slices/authSlice';
import { setStreakCount } from '../redux/userSlice';
interface RegisterUserData {
  referralCode?: string;
  counsellorCode?: string;
}

export const useRegisterUser = () => {
  const dispatch = useDispatch();
  const api = useApi();

  const registerUser = useCallback(async (userData: RegisterUserData) => {
    const maxRetries = 3;
    const retryDelay = 2000;
    const timeout = 10000;

    let attempt = 0;
    while (attempt < maxRetries) {
      try {
        const response = await Promise.race([
          api.post('/auth/register', userData),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Request timed out')), timeout))
        ]) as any;

        // Update Redux store with user data
        dispatch(setUser(response.data.user));
        dispatch(setAvatar(response.data.user.avatar));
        dispatch(setPhoneVerified(response?.data?.user?.phoneVerified));
        dispatch(setStreakCount(response?.data?.user?.loginStreak));

        try {
          if (response.status === 201) {
            try { sessionStorage.setItem('register_status', '201'); } catch (e) { console.error("Error", e); }
            try { window.dispatchEvent(new Event('registerSuccess')); } catch (e) { console.error("Error", e); }
          }
        } catch (e) {
          console.error("Error", e);
        }

        return response.data;
      } catch (error) {
        const err: any = error;
        const status = err?.response?.status;

        // If this is a session-expired error, let the interceptor handle it
        if (err?.isSessionExpired || status === 403) {
          // Don't retry, let the interceptor navigation take precedence
          throw error;
        }

        // If it's a client error (4xx), don't retry — return the server message to the caller
        if (status && status >= 400 && status < 500) {
          const serverMessage = err?.response?.data?.message || (err instanceof Error ? err.message : 'Request failed');
          console.error('Register failed with client error:', { status, serverMessage });
          throw new Error(serverMessage);
        }

        attempt++;
        if (attempt >= maxRetries) {
          console.error('Error registering user after 3 attempts:', {
            message: err instanceof Error ? err.message : 'Unknown error',
            data: err,
          });
          throw new Error('Oops! Something went wrong. Please try again later.');
        }

        // backoff before retrying
        await new Promise(res => setTimeout(res, retryDelay));
      }
    }
  }, [dispatch, api]);

  return { registerUser };
};